<template>
  <vbt-content-box
    :loading="isLoading"
    :loading-error="loadingError"
    :no-content="!replacements.length"
    title="Information"
  >
    <template #toolbar>
      <v-btn
        x-small
        color="success"
        @click="showCreateReplacementDialog"
      >
        Create replacement
      </v-btn>
    </template>

    <vbt-table
      :headers="headers"
      :items="replacements"
      :page-size="replacements.length"
    >
      <template #from="{ item: { from: _date } }">
        {{ formatDisplayDate(_date, { dateFormat: 'yyyy-MM-dd' }) }} {{ _date ? 'UTC+0' : '' }}
      </template>

      <template #to="{ item: { to: _date } }">
        <template v-if="!_date">
          -
        </template>
        <pb-date-time
          v-else
          :date="_date.replace('Z', '')"
          format="yyyy-MM-dd"
        />
      </template>

      <template #substrate="{ item }">
        {{ generateReplacementStringFromObj(item.substrate || {}) }}
      </template>

      <template #replacement="{ item }">
        {{ generateReplacementStringFromObj(item.replacement || {}) }}
      </template>

      <template #actions="{ item }">
        <v-layout
          justify-space-between
          align-center
        >
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn
                icon
                small
                :color="item.enabled ? 'success' : 'error'"
                v-on="on"
                @click="changeReplacementState(item.id, item.enabled)"
              >
                <v-icon>
                  {{ item.enabled ? 'toggle_on': 'toggle_off' }}
                </v-icon>
              </v-btn>
            </template>

            {{ item.enabled ? 'Disable' : 'Enable' }} replacement
          </v-tooltip>

          <v-btn
            icon
            small
            color="warning"
            @click="showEditReplacementDialog(item)"
          >
            <v-icon>
              edit
            </v-icon>
          </v-btn>

          <v-btn
            icon
            small
            color="error"
            @click="removeReplacement(item.id)"
          >
            <v-icon>
              delete
            </v-icon>
          </v-btn>
        </v-layout>
      </template>
    </vbt-table>

    <pb-replacement-editor
      v-model="replacementEditorDialog"
      :create="replacementEditorIsCreator"
      :replacement="replacementEditorData"
      @success="$_fetchData"
    />
  </vbt-content-box>
</template>

<script>
import { mapState } from 'vuex';

import { useDataLoading } from '@mixins/factories';

import { formatDisplayDate, wrapToLoadingFn } from '@helpers';

import { LOCATIONS_NAMESPACE } from '@store/modules/locations.types';

import { generateReplacementStringFromObj } from '../_helpers/replacementString';

import { ReplacementsApiService } from '../_services/replacements.api.service';

import PbReplacementEditor from '../_components/PbReplacementEditor.vue';

const dataLoadingOptions = {
  getterName: 'getReplacements',
  searchable: false,
  paged: false,
};

const headers = Object.freeze([
  { text: 'From', value: 'from' },
  { text: 'To', value: 'to' },
  { text: 'Substrate', value: 'substrate' },
  { text: 'Replacement', value: 'replacement' },
  { text: '', value: 'actions', width: '130px' },
]);

export default {
  name: 'PbReplacements',

  components: {
    PbReplacementEditor,
  },

  mixins: [
    useDataLoading(dataLoadingOptions),
  ],

  data() {
    return {
      replacementEditorIsCreator: false,
      replacementEditorDialog: false,
      replacementEditorData: {},

      replacements: [],

      headers,
    };
  },

  computed: {
    ...mapState(LOCATIONS_NAMESPACE, ['suffix']),
  },

  methods: {
    getReplacements({ suffix }) {
      return this.wrapToLoadingFn({
        req: ReplacementsApiService.getReplacements.bind({}, { suffix }),
        onSuccess: (r) => {
          this.replacements = r;
        },
      });
    },

    removeReplacement(id) {
      this.$VBlackerTheme.alert.warning({
        text: 'Remove replacement?',
      }, () => this.wrapToLoadingFn({
        req: ReplacementsApiService.removeReplacement.bind({}, {
          suffix: this.suffix,
          id,
        }),
        fullscreenLoadingSpinner: true,
        onSuccess: () => {
          this.$VBlackerTheme.notification.success('Successfully removed');
          this.$_fetchData();
        },
      }));
    },

    changeReplacementState(id, currentState) {
      this.$VBlackerTheme.alert.warning({
        text: `${currentState ? 'Disable' : 'Enable'} replacement?`,
      }, () => this.wrapToLoadingFn({
        req: ReplacementsApiService.changeReplacementState.bind({}, {
          state: !currentState,
          suffix: this.suffix,
          id,
        }),
        fullscreenLoadingSpinner: true,
        onSuccess: () => {
          this.$VBlackerTheme.notification.success(`Successfully ${currentState ? 'disabled' : 'enabled'}`);
          this.$_fetchData();
        },
      }));
    },

    showCreateReplacementDialog() {
      this.replacementEditorIsCreator = true;
      this.replacementEditorDialog = true;
    },

    showEditReplacementDialog(data) {
      this.$set(this, 'replacementEditorData', data);
      this.replacementEditorIsCreator = false;
      this.replacementEditorDialog = true;
    },

    generateReplacementStringFromObj,
    formatDisplayDate,
    wrapToLoadingFn,
  },
};
</script>
